// Error code catalogue and AppError [02 §6.2; BR-API-001/002]. No user-facing text here: every error
// carries an i18n `messageKey` (`errors.<namespace>.<name>`); the API layer localises it (EN/FR).

/** code → HTTP status. 02 §6.2 table first, then module-specific codes cited by chapters 04–07. */
export const ERROR_CODES = Object.freeze({
  // 02 §6.2
  VALIDATION_FAILED: 400,
  UNAUTHENTICATED: 401,
  MFA_REQUIRED: 401,
  SESSION_EXPIRED: 401,
  STEP_UP_REQUIRED: 401,
  FORBIDDEN: 403,
  SOD_SAME_ACTOR: 403,
  APPROVAL_LIMIT_EXCEEDED: 403,
  DEVICE_SCOPE: 403,
  NOT_FOUND: 404,
  GATE_BLOCKED: 409,
  STATE_CONFLICT: 409,
  INSUFFICIENT_AVAILABLE: 409,
  PERIOD_LOCKED: 409,
  DUPLICATE_DOCUMENT: 409,
  IDEMPOTENCY_IN_PROGRESS: 409,
  IDEMPOTENCY_KEY_REUSED: 409,
  /** CR-W2a-42 R1 / CR-W2b-12: the first answer issued a secret, so it was never stored and cannot be replayed. */
  IDEMPOTENCY_NOT_REPLAYABLE: 409,
  STALE_VERSION: 412,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  BUSINESS_RULE: 422,
  STATUS_NOT_PATCHABLE: 422,
  PRECONDITION_REQUIRED: 428,
  RATE_LIMITED: 429,
  INTERNAL: 500,
  /** Frozen stubs whose owning lane has not landed the body yet [BUILD-PLAN §5]. */
  NOT_IMPLEMENTED: 501,
  DEPENDENCY_UNAVAILABLE: 503,
  // chapters 04–07 (module-specific refusals)
  REASON_REQUIRED: 400,
  COLOUR_NOT_IN_CATALOGUE: 400,
  ALREADY_ORDERED: 409,
  DUPLICATE_ROLL: 409,
  DUPLICATE_SUPPLIER_INVOICE: 409,
  HELD_STOCK: 409,
  IN_USE: 409,
  LOT_MIX: 409,
  OVERSHIP_TOLERANCE: 409,
  PARALLEL_MODE_EXTERNAL_ACTION: 409,
  PO_HAS_RECEIPTS: 409,
  PO_NOT_RECEIVABLE: 409,
  RECEIPT_CONSUMED: 409,
  FROZEN_SETTING: 409,
  CONTROL_ACCOUNT: 422,
  FX_RATE_MISSING: 422,
  NEUTRAL_TRIM_NO_COLOURS: 422,
  OVER_RECEIPT_REASON_REQUIRED: 422,
  QTY_BELOW_RECEIVED: 422,
  CURRENCY_MISMATCH: 422,
  UOM_MISMATCH: 422,
} as const);

export type ErrorCode = keyof typeof ERROR_CODES;
export const ERROR_CODE_LIST = Object.freeze(Object.keys(ERROR_CODES) as ErrorCode[]);

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.hasOwn(ERROR_CODES, value);
}

export function httpStatusOf(code: ErrorCode): number {
  return ERROR_CODES[code];
}

/** Default i18n key for a code: `errors.code.<CODE>` (callers may pass a more specific key). */
export function defaultMessageKey(code: ErrorCode): string {
  return `errors.code.${code}`;
}

export type ErrorParams = Readonly<Record<string, string | number | boolean | null>>;

/** One field-level problem; `path` uses dot notation, grid paths `lines.{i}.qty.{size}`. */
export interface FieldError {
  readonly path: string;
  readonly code: string;
  readonly messageKey: string;
  readonly params?: ErrorParams;
}

export interface AppErrorOptions {
  readonly messageKey?: string;
  readonly params?: ErrorParams;
  readonly fields?: readonly FieldError[];
  readonly gate?: string;
  readonly overridable?: boolean;
  /** Extra machine data for the client (e.g. `current` representation on STALE_VERSION). */
  readonly details?: Readonly<Record<string, unknown>>;
  readonly cause?: unknown;
}

/** The only error type services throw for expected failures; anything else maps to INTERNAL. */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly messageKey: string;
  readonly params: ErrorParams;
  readonly fields: readonly FieldError[];
  readonly gate: string | undefined;
  readonly overridable: boolean;
  readonly details: Readonly<Record<string, unknown>> | undefined;

  constructor(code: ErrorCode, options: AppErrorOptions = {}) {
    // `message` is a log-only technical string (the code + key), never shown to users.
    super(`${code}: ${options.messageKey ?? defaultMessageKey(code)}`, { cause: options.cause });
    this.name = 'AppError';
    this.code = code;
    this.status = ERROR_CODES[code];
    this.messageKey = options.messageKey ?? defaultMessageKey(code);
    this.params = Object.freeze({ ...(options.params ?? {}) });
    this.fields = Object.freeze([...(options.fields ?? [])]);
    this.gate = options.gate;
    this.overridable = options.overridable ?? false;
    this.details = options.details;
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/** Wraps any thrown value: AppError passes through, everything else becomes INTERNAL (no leak). */
export function toAppError(value: unknown): AppError {
  if (isAppError(value)) return value;
  return new AppError('INTERNAL', { cause: value });
}

// ---- factories for the common cases (keep call sites short and consistent) ----

export const errors = Object.freeze({
  validation: (fields: readonly FieldError[], messageKey?: string) =>
    new AppError('VALIDATION_FAILED', { fields, ...(messageKey ? { messageKey } : {}) }),
  notFound: (entity: string) =>
    new AppError('NOT_FOUND', { messageKey: 'errors.code.NOT_FOUND', params: { entity } }),
  forbidden: (permission: string) => new AppError('FORBIDDEN', { params: { permission } }),
  stateConflict: (from: string, action: string) =>
    new AppError('STATE_CONFLICT', { params: { from, action } }),
  staleVersion: (current?: unknown) =>
    new AppError('STALE_VERSION', current === undefined ? {} : { details: { current } }),
  businessRule: (messageKey: string, params?: ErrorParams) =>
    new AppError('BUSINESS_RULE', { messageKey, ...(params ? { params } : {}) }),
  internal: (cause?: unknown) => new AppError('INTERNAL', { cause }),
});
